import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUp, ArrowDown, Storefront, MegaphoneSimple, Sparkle, CaretDown, CaretRight } from '@phosphor-icons/react';
import { formatDate } from '@/utils/datetime';
import FounderboardCoinIcon from './FounderboardCoinIcon';

// Founderboard Coins wallet — a section of the startup Billing page. Ledger and
// balance live in backend/coins.py; redemption options are not live yet.
// Billing fetches GET /coins/wallet once and passes it here and to CoinsBalanceChip.

const KIND_LABEL = { grant: 'Granted by Founderboard', earn: 'Earned', deduct: 'Adjusted by Founderboard', redeem: 'Redeemed' };
const coinsFmt = (n) => Number(n || 0).toLocaleString('en-IN');
const PREVIEW_ROWS = 5;

const COMING_SOON = [
  { icon: Storefront, title: 'Services', body: 'Put coins towards managed services.' },
  { icon: MegaphoneSimple, title: 'Marketing', body: 'Use coins on campaigns and engagements.' },
  { icon: Sparkle, title: 'Platform perks', body: 'Unlock premium features and partner offers.' },
];

export const CoinsBalanceChip = ({ wallet }) => {
  if (!wallet) return null;
  const jump = () => {
    window.history.replaceState(null, '', '#coins');
    document.getElementById('coins')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <button type="button" onClick={jump} data-testid="coins-balance-chip"
      title="Founderboard Coins — view wallet"
      className="inline-flex items-center gap-2.5 rounded-full border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 pl-1.5 pr-4 py-1.5 transition-colors shrink-0">
      <FounderboardCoinIcon size={32} title="Founderboard Coin" />
      <span className="text-left leading-tight">
        <span className="block text-lg font-heading font-black text-text-primary tabular-nums">{coinsFmt(wallet.balance)}</span>
        <span className="block text-[10px] uppercase tracking-wider font-semibold text-text-muted">FB Coins</span>
      </span>
    </button>
  );
};

const CoinsWallet = ({ wallet }) => {
  const { hash } = useLocation();
  const [showAll, setShowAll] = useState(false);

  // Deep links (/startup/coins redirects to /startup/billing#coins) land on this section.
  useEffect(() => {
    if (wallet && hash === '#coins') {
      document.getElementById('coins')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [wallet, hash]);

  if (!wallet) return null;
  const txns = wallet.transactions || [];
  const shown = showAll ? txns : txns.slice(0, PREVIEW_ROWS);

  return (
    <section id="coins" className="border border-amber-500/30 rounded-2xl bg-surface p-6 my-5 scroll-mt-6" data-testid="coins-wallet">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <FounderboardCoinIcon size={44} />
          <div>
            <h2 className="font-heading font-bold text-lg text-text-primary leading-tight">Founderboard Coins</h2>
            <p className="text-xs text-text-secondary">Shared by everyone on your team.</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-heading font-black text-text-primary tabular-nums" data-testid="coins-balance">{coinsFmt(wallet.balance)}</div>
          <div className="text-[11px] text-text-muted">
            coins available · {coinsFmt(wallet.totals?.earned)} received · {coinsFmt(wallet.totals?.spent)} used
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-background p-4 mb-5" data-testid="coins-redeem-soon">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-semibold text-text-primary">Ways to use your coins</span>
          <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">Coming soon</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {COMING_SOON.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex items-start gap-2">
              <Icon size={16} weight="bold" className="text-text-muted mt-0.5 shrink-0" />
              <div>
                <div className="text-xs font-semibold text-text-primary">{title}</div>
                <div className="text-[11px] text-text-secondary">{body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div data-testid="coins-history">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted mb-2">Coin history</div>
        {txns.length === 0 ? (
          <p className="text-sm text-text-muted" data-testid="coins-history-empty">No coin activity yet.</p>
        ) : (
          <>
            <div className="divide-y divide-border">
              {shown.map((t) => {
                const credit = t.amount > 0;
                return (
                  <div key={t.id} className="flex items-center gap-3 py-2.5" data-testid={`coins-txn-${t.id}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${credit ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-danger/10 text-danger'}`}>
                      {credit ? <ArrowDown size={12} weight="bold" /> : <ArrowUp size={12} weight="bold" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">{t.reason || KIND_LABEL[t.kind] || 'Coin movement'}</div>
                      <div className="text-[11px] text-text-muted">{KIND_LABEL[t.kind] || t.kind} · {formatDate(t.created_at)}</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono font-bold text-sm tabular-nums ${credit ? 'text-emerald-600 dark:text-emerald-400' : 'text-danger'}`}>
                        {credit ? '+' : '−'}{coinsFmt(Math.abs(t.amount))}
                      </div>
                      <div className="text-[10px] text-text-muted tabular-nums">bal {coinsFmt(t.balance_after)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            {txns.length > PREVIEW_ROWS && (
              <button onClick={() => setShowAll((s) => !s)} data-testid="coins-history-toggle"
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-text-secondary hover:text-text-primary">
                {showAll ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                {showAll ? 'Show less' : `Show all ${txns.length}`}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default CoinsWallet;
